import { ChangeEvent, useMemo, useState } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { DEFAULT_AXNUI_THEME } from "@iddataweb/idw-common-ui/constants";
import { resources } from "@constants/hardcoded-strings";
import { BOOLEAN_RADIO_INPUT_OPTIONS } from "@constants/boolean-radio-input-options";
import { AXNUIThemingTabTypes } from "@enums/axnui-theming-tab-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { AXNUITheme, AXNUIThemeCustomCSS } from "@iddataweb/idw-common-ui/types";
import { ThemingCustomCssValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { ContentPageBody } from "../../../../common/content-page-body/content-page-body";
import { ContentPageHeader } from "../../../../common/content-page-header/content-page-header";
import { ThemingFormContainer } from "../../../../common/theming-form-container/theming-form-container";
import { ThemingConfigurationContainer } from "../../../../common/theming-configuration-container/theming-configuration-container";
import { ThemingSubHeaderContainer } from "../../../../common/theming-sub-header-container/theming-sub-header-container";
import { ThemingFormSection } from "../../../../common/theming-form-section/theming-form-section";
import { RadioInputFormGroup } from "../../../../common/radio-input-form-group/radio-input-form-group";
import { TextareaFormGroup } from "../../../../common/textarea-form-group/textarea-form-group";

type ThemingCustomCssTabContainerProps = {
    isDisabled: boolean;
    validationErrors: ThemingCustomCssValidationErrors;
    axnuiTheme: AXNUITheme;
    generatePreviewComponent: (currentTab: AXNUIThemingTabTypes) => React.ReactNode;
    updateAXNUIThemePropertyValue: (axnuiTheme: AXNUITheme) => void;
};

export const ThemingCustomCssTabContainer = (props: ThemingCustomCssTabContainerProps) => {
    const {
        isDisabled,
        validationErrors,
        axnuiTheme,
        generatePreviewComponent,
        updateAXNUIThemePropertyValue
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const updateAXNUIThemeCustomCssFieldValue = (key: keyof AXNUIThemeCustomCSS, value: string | boolean) => {
        const newPropertyValue: AXNUITheme = {
            ...axnuiTheme,
            customCss: {
                ...axnuiTheme.customCss,
                [key]: value
            }
        };
        updateAXNUIThemePropertyValue(newPropertyValue);
    };

    const handleUseCustomCssChange = (newValue: string) => {
        updateAXNUIThemeCustomCssFieldValue("useCustomCss", isStringTrue(newValue));
    };

    const handleResetUseCustomCss = () => {
        updateAXNUIThemeCustomCssFieldValue("useCustomCss", DEFAULT_AXNUI_THEME.customCss.useCustomCss);
    };

    const handleCustomCssContentChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIThemeCustomCssFieldValue("cssContent", event.target.value);
    };

    const handleResetCustomCssContent = () => {
        updateAXNUIThemeCustomCssFieldValue("cssContent", DEFAULT_AXNUI_THEME.customCss.cssContent);
    };

    const previewComponent = useMemo(() => {
        return generatePreviewComponent(AXNUIThemingTabTypes.CUSTOM_CSS);
    }, [generatePreviewComponent]);

    return (
        <div>
            <ContentPageHeader title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.customCss.title} />
            <ContentPageBody isMaxWidth={true}>
                <ThemingSubHeaderContainer description={resources.pages.verificationServiceDetails.tabs.theming.subTabs.customCss.description} />
                <ThemingConfigurationContainer>
                    <ThemingFormContainer>
                        <ThemingFormSection showSeparator={axnuiTheme.customCss.useCustomCss}>
                            <RadioInputFormGroup
                                controlId={`theming-custom-css-use-custom-css-radio-input`}
                                automationId={`theming-custom-css-use-custom-css-radio-input`}
                                label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.customCss.inputs.useCustomCss.label}
                                layout={layout}
                                labelWidth={labelWidth}
                                onChange={handleUseCustomCssChange}
                                options={BOOLEAN_RADIO_INPUT_OPTIONS}
                                value={axnuiTheme.customCss.useCustomCss ? "true" : "false"}
                                disabled={isDisabled}
                                isRequired={true}
                                afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.customCss.inputs.useCustomCss.tooltip}
                                afterLabelIcon={isDisabled ? null : faUndo}
                                afterLabelIconOnClick={handleResetUseCustomCss}
                            />
                        </ThemingFormSection>
                        {axnuiTheme.customCss.useCustomCss && (
                            <ThemingFormSection>
                                <TextareaFormGroup
                                    controlId={"theming-custom-css-custom-css-content-input"}
                                    automationId={"theming-custom-css-custom-css-content-input"}
                                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.customCss.inputs.customCss.label}
                                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.customCss.inputs.customCss.label}
                                    layout={layout}
                                    labelWidth={labelWidth}
                                    onChange={handleCustomCssContentChange}
                                    value={axnuiTheme.customCss.cssContent || ""}
                                    disabled={isDisabled}
                                    isRequired={true}
                                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.customCss.inputs.customCss.tooltip}
                                    afterLabelIcon={isDisabled ? null : faUndo}
                                    afterLabelIconOnClick={handleResetCustomCssContent}
                                />
                                {validationErrors.customCssError}
                            </ThemingFormSection>
                        )}
                    </ThemingFormContainer>
                    {previewComponent}
                </ThemingConfigurationContainer>
            </ContentPageBody>
        </div >
    );
};